import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * MagicHummingbird — a living, breathing gold-dust kolibri that materialises,
 * darts, hovers, loops and figure-8s across the viewport like a real hummingbird,
 * then dematerialises in a burst of particles.
 *
 * Flight modes (weighted): sweep, loop, figure_eight, hover_dart, courtship_dive.
 * Pure RAF + DOM. Honours prefers-reduced-motion. Skips admin/auth/checkout.
 */
const COLORS = ["#FFE9A8", "#F6D274", "#D4AF37", "#B8902A", "#FFF6D8"];
const SKIP_PATTERNS = [/^\/admin/, /^\/auth/, /^\/checkout/];

const rand = (a, b) => Math.random() * (b - a) + a;
const randInt = (a, b) => Math.floor(rand(a, b + 1));
const lerp = (a, b, t) => a + (b - a) * t;
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// Pick a weighted random
const pickMode = () => {
  const modes = [
    ["sweep", 3],
    ["loop", 3],
    ["figure_eight", 2],
    ["hover_dart", 2],
    ["courtship_dive", 1],
  ];
  const total = modes.reduce((s, m) => s + m[1], 0);
  let r = Math.random() * total;
  for (const [name, w] of modes) {
    if ((r -= w) <= 0) return name;
  }
  return "sweep";
};

// Build a parametric path function for a given mode + viewport dims.
const buildPath = (mode, W, H) => {
  const fromLeft = Math.random() > 0.5;
  const sx = fromLeft ? -80 : W + 80;
  const ex = fromLeft ? W + 80 : -80;
  const sy = rand(H * 0.20, H * 0.55);
  const ey = rand(H * 0.25, H * 0.60);
  // central anchor of acrobatics
  const cx = lerp(sx, ex, rand(0.35, 0.65));
  const cy = rand(H * 0.25, H * 0.55);
  const r = Math.min(W, H) * rand(0.08, 0.14);
  const dir = fromLeft ? 1 : -1;

  if (mode === "sweep") {
    // Two-control cubic bezier across the viewport — graceful S
    const cx1 = lerp(sx, ex, 0.30);
    const cy1 = sy + rand(-H * 0.18, -H * 0.04);
    const cx2 = lerp(sx, ex, 0.70);
    const cy2 = ey + rand(H * 0.04, H * 0.18);
    return {
      duration: rand(4400, 5800),
      mode,
      dir,
      fn: (t) => {
        const u = 1 - t;
        return {
          x: u*u*u*sx + 3*u*u*t*cx1 + 3*u*t*t*cx2 + t*t*t*ex,
          y: u*u*u*sy + 3*u*u*t*cy1 + 3*u*t*t*cy2 + t*t*t*ey,
        };
      },
    };
  }

  if (mode === "loop") {
    // Approach (0–0.25) → 2 vertical loops (0.25–0.80) → exit (0.80–1)
    const loops = randInt(2, 3);
    return {
      duration: rand(5200, 6800),
      mode,
      dir,
      fn: (t) => {
        if (t < 0.25) {
          const u = easeOutCubic(t / 0.25);
          return { x: lerp(sx, cx + r * dir, u), y: lerp(sy, cy, u) };
        }
        if (t < 0.80) {
          const u = (t - 0.25) / 0.55;
          const a = u * Math.PI * 2 * loops - Math.PI / 2;
          return {
            x: cx + Math.cos(a) * r * dir,
            y: cy + Math.sin(a) * r,
          };
        }
        const u = easeOutCubic((t - 0.80) / 0.20);
        const startX = cx + Math.cos(-Math.PI / 2) * r * dir;
        const startY = cy + Math.sin(-Math.PI / 2) * r;
        return { x: lerp(startX, ex, u), y: lerp(startY, ey, u) };
      },
    };
  }

  if (mode === "figure_eight") {
    // Lemniscate of Bernoulli — proper figure-8
    const loops = 2;
    return {
      duration: rand(5600, 7000),
      mode,
      dir,
      fn: (t) => {
        if (t < 0.18) {
          const u = easeOutCubic(t / 0.18);
          return { x: lerp(sx, cx - r * dir, u), y: lerp(sy, cy, u) };
        }
        if (t < 0.85) {
          const u = (t - 0.18) / 0.67;
          const a = u * Math.PI * 2 * loops;
          const denom = 1 + Math.sin(a) * Math.sin(a);
          return {
            x: cx + (r * 1.6 * Math.cos(a)) / denom * dir,
            y: cy + (r * 0.9 * Math.sin(a) * Math.cos(a)) / denom,
          };
        }
        const u = easeOutCubic((t - 0.85) / 0.15);
        return { x: lerp(cx + r * 1.6 * dir / 2, ex, u), y: lerp(cy, ey, u) };
      },
    };
  }

  if (mode === "hover_dart") {
    // enter → hover1 (with bob) → dart → hover2 → exit
    const p1x = lerp(sx, ex, 0.30);
    const p1y = rand(H * 0.25, H * 0.50);
    const p2x = lerp(sx, ex, 0.65);
    const p2y = rand(H * 0.30, H * 0.55);
    return {
      duration: rand(5800, 7400),
      mode,
      dir,
      fn: (t) => {
        if (t < 0.18) {
          const u = easeOutCubic(t / 0.18);
          return { x: lerp(sx, p1x, u), y: lerp(sy, p1y, u) };
        }
        if (t < 0.40) {
          // Hover with vertical bob
          const u = (t - 0.18) / 0.22;
          return {
            x: p1x + Math.sin(u * Math.PI * 4) * 6,
            y: p1y + Math.sin(u * Math.PI * 5) * 10,
          };
        }
        if (t < 0.55) {
          // Dart — fast & slightly curved
          const u = easeInOut((t - 0.40) / 0.15);
          const arcY = -Math.sin(u * Math.PI) * 30;
          return { x: lerp(p1x, p2x, u), y: lerp(p1y, p2y, u) + arcY };
        }
        if (t < 0.80) {
          const u = (t - 0.55) / 0.25;
          return {
            x: p2x + Math.sin(u * Math.PI * 4) * 6,
            y: p2y + Math.sin(u * Math.PI * 5) * 10,
          };
        }
        const u = easeOutCubic((t - 0.80) / 0.20);
        return { x: lerp(p2x, ex, u), y: lerp(p2y, ey, u) };
      },
    };
  }

  // courtship_dive — J-shaped swoop: climb, hover at apex, dive, climb out
  const apexX = lerp(sx, ex, 0.50);
  const apexY = H * rand(0.12, 0.22);
  const bottomY = H * rand(0.55, 0.70);
  return {
    duration: rand(5400, 6600),
    mode: "courtship_dive",
    dir,
    fn: (t) => {
      if (t < 0.30) {
        // Climb to apex (curve up)
        const u = easeOutCubic(t / 0.30);
        const arc = Math.sin(u * Math.PI / 2);
        return { x: lerp(sx, apexX, u), y: lerp(sy, apexY, arc) };
      }
      if (t < 0.45) {
        // Hover at apex with small bob
        const u = (t - 0.30) / 0.15;
        return { x: apexX + Math.sin(u * Math.PI * 2) * 8, y: apexY + Math.sin(u * Math.PI * 3) * 6 };
      }
      if (t < 0.70) {
        // Dive down
        const u = easeInOut((t - 0.45) / 0.25);
        return { x: lerp(apexX, lerp(apexX, ex, 0.30), u), y: lerp(apexY, bottomY, u) };
      }
      // Climb out
      const u = easeOutCubic((t - 0.70) / 0.30);
      return { x: lerp(lerp(apexX, ex, 0.30), ex, u), y: lerp(bottomY, ey, u) };
    },
  };
};

const MagicHummingbird = () => {
  const stageRef = useRef(null);
  const loc = useLocation();
  const stateRef = useRef({ raf: 0, timer: 0 });

  useEffect(() => {
    if (SKIP_PATTERNS.some((re) => re.test(loc.pathname))) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const stage = stageRef.current;
    if (!stage) return undefined;

    let cancelled = false;

    const spawnParticle = (x, y, opts = {}) => {
      const el = document.createElement("span");
      const size = opts.size ?? rand(2, 6);
      const color = COLORS[randInt(0, COLORS.length - 1)];
      el.className = "magic-particle";
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.width = `${size}px`;
      el.style.height = `${size}px`;
      el.style.background = color;
      el.style.boxShadow = `0 0 ${size * 2}px ${color}`;
      const dx = opts.dx ?? rand(-30, 30);
      const dy = opts.dy ?? rand(-50, -10);
      const dur = opts.duration ?? rand(900, 1800);
      el.style.setProperty("--dx", `${dx}px`);
      el.style.setProperty("--dy", `${dy}px`);
      el.style.setProperty("--dur", `${dur}ms`);
      stage.appendChild(el);
      setTimeout(() => el.remove(), dur + 80);
    };

    const burst = (x, y, count) => {
      for (let i = 0; i < count; i += 1) {
        const a = (Math.PI * 2 * i) / count + rand(-0.3, 0.3);
        const r = rand(20, 70);
        spawnParticle(x, y, {
          dx: Math.cos(a) * r,
          dy: Math.sin(a) * r,
          duration: rand(800, 1500),
          size: rand(2, 5),
        });
      }
    };

    const flight = () => {
      if (cancelled) return;

      const W = window.innerWidth;
      const H = window.innerHeight;
      const mode = pickMode();
      const path = buildPath(mode, W, H);

      const wrap = document.createElement("div");
      wrap.className = "magic-bird";
      const wingBlur = document.createElement("span");
      wingBlur.className = "magic-bird-wing-blur";
      const img = document.createElement("img");
      img.src = "/brand/hummingbird-gold.png";
      img.alt = "";
      img.draggable = false;
      wrap.appendChild(wingBlur);
      wrap.appendChild(img);
      stage.appendChild(wrap);

      const size = Math.max(60, Math.min(108, W * 0.07));
      wrap.style.width = `${size}px`;
      wrap.style.height = `${size}px`;
      wrap.style.opacity = "0";

      const startedAt = performance.now();
      const materialise = 480;
      const dematerialise = 540;

      // initial converging particles
      const p0 = path.fn(0);
      burst(p0.x, p0.y, 16);

      let prev = { x: p0.x, y: p0.y };
      let lastTrailAt = 0;

      const tick = (now) => {
        if (cancelled) return;
        const elapsed = now - startedAt;
        const t = Math.min(1, elapsed / path.duration);
        const pt = path.fn(t);

        // velocity for heading + speed-based effects
        const vx = pt.x - prev.x;
        const vy = pt.y - prev.y;
        const speed = Math.hypot(vx, vy);
        let angle = Math.atan2(vy, vx) * (180 / Math.PI);
        prev = { x: pt.x, y: pt.y };

        // For looping/figure-8 we let the bird rotate fully (more dramatic).
        // For other modes, clamp tilt so the silhouette stays readable.
        let visualAngle;
        if (path.mode === "loop" || path.mode === "figure_eight" || path.mode === "courtship_dive") {
          visualAngle = path.dir > 0 ? angle : angle + 180;
        } else {
          // tame the tilt: scale down + clamp
          const tilt = Math.max(-32, Math.min(32, angle * 0.35));
          visualAngle = path.dir > 0 ? tilt : 180 - tilt;
        }

        // Materialise / dematerialise opacity & scale
        let op = 0.95;
        let scale = 1;
        if (elapsed < materialise) {
          const u = elapsed / materialise;
          op = 0.95 * u;
          scale = 0.4 + 0.6 * u;
        } else if (elapsed > path.duration - dematerialise) {
          const u = Math.max(0, (path.duration - elapsed) / dematerialise);
          op = 0.95 * u;
          scale = 0.45 + 0.55 * u;
        }

        wrap.style.opacity = String(op);
        wrap.style.transform = `translate(${pt.x - size / 2}px, ${pt.y - size / 2}px) rotate(${visualAngle}deg) scale(${scale})`;

        // Wing-blur intensity scales with speed (fast = more blur)
        const blurOp = Math.min(0.85, 0.25 + speed * 0.07);
        wingBlur.style.opacity = String(blurOp);

        // Sparkle trail — emit more particles when slower (hovering) for shimmer
        // and fewer at high speed (cleaner streak)
        const emitGap = speed > 8 ? 60 : 36;
        if (elapsed - lastTrailAt > emitGap && t > 0.05 && t < 0.95) {
          lastTrailAt = elapsed;
          spawnParticle(pt.x + rand(-10, 10), pt.y + rand(-10, 10), {
            dx: rand(-10, 10),
            dy: rand(-28, -4),
            duration: rand(800, 1600),
            size: rand(1.5, 4.5),
          });
          // double sparkle during loops & dives for drama
          if (path.mode !== "sweep" && Math.random() < 0.35) {
            spawnParticle(pt.x + rand(-14, 14), pt.y + rand(-14, 14), {
              dx: rand(-18, 18),
              dy: rand(-22, 4),
              duration: rand(700, 1300),
              size: rand(1, 3.5),
            });
          }
        }

        if (t < 1) {
          stateRef.current.raf = requestAnimationFrame(tick);
        } else {
          burst(pt.x, pt.y, 20);
          wrap.remove();
          if (cancelled) return;
          stateRef.current.timer = window.setTimeout(flight, randInt(18000, 35000));
        }
      };

      stateRef.current.timer = window.setTimeout(() => {
        stateRef.current.raf = requestAnimationFrame(tick);
      }, 280);
    };

    const onVisibility = () => {
      if (document.hidden) {
        clearTimeout(stateRef.current.timer);
        cancelAnimationFrame(stateRef.current.raf);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    stateRef.current.timer = window.setTimeout(flight, randInt(3000, 6500));

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimeout(stateRef.current.timer);
      cancelAnimationFrame(stateRef.current.raf);
      while (stage.firstChild) stage.removeChild(stage.firstChild);
    };
  }, [loc.pathname]);

  return <div ref={stageRef} className="magic-stage" aria-hidden="true" data-testid="magic-hummingbird-stage" />;
};

export default MagicHummingbird;
